// JS stick-to-bottom scrolling for `<Chat>` (fn-48.3, R5).
//
// Safari has NO `overflow-anchor`, so CSS-only pinning silently breaks
// on iOS/macOS — the discrimination logic is implemented in JS (the
// small version of use-stick-to-bottom's approach; the dependency
// itself is NOT added — this package stays dependency-light):
//
//   - Track whether the user is at (or near) the tail via a
//     distance-from-bottom threshold on scroll.
//   - When a NEW tail lands (`tailKey` change): scroll the bottom
//     sentinel into view ONLY when the user was already following the
//     tail. Reading history is never yanked.
//   - `prefers-reduced-motion` → `behavior: 'instant'` (no smooth
//     glide), checked at scroll time.
//
// `scrollIntoView` is feature-guarded for non-browser test DOMs.
import { useEffect, useRef, useState } from 'react';
/** How close to the bottom (px) still counts as "following the tail". */
export const NEAR_BOTTOM_THRESHOLD_PX = 72;
/** Reduced-motion media query — shared by scroll + indicator styling. */
export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
/** One-shot reduced-motion check (scroll-time; no subscription). */
export function prefersReducedMotion() {
    return globalThis.matchMedia?.(REDUCED_MOTION_QUERY).matches === true;
}
/**
 * Subscribed reduced-motion flag — drives the working indicator's
 * animation gate (the CSS `@media` rule is the belt; this is the
 * braces for the JS-rendered class, and what tests can reach via a
 * `matchMedia` mock).
 */
export function usePrefersReducedMotion() {
    const [reduced, setReduced] = useState(prefersReducedMotion);
    useEffect(() => {
        const mql = globalThis.matchMedia?.(REDUCED_MOTION_QUERY);
        if (mql === undefined)
            return;
        setReduced(mql.matches);
        const onChange = () => setReduced(mql.matches);
        mql.addEventListener?.('change', onChange);
        return () => mql.removeEventListener?.('change', onChange);
    }, []);
    return reduced;
}
/**
 * Stick-to-bottom controller. `tailKey` identifies the current tail —
 * pass something that changes whenever a new item lands (e.g.
 * `${lastId}:${count}`); `null` disables auto-scroll entirely.
 */
export function useStickToBottom(tailKey) {
    const containerRef = useRef(null);
    const bottomRef = useRef(null);
    // Starts true — a fresh mount follows the live tail until the user
    // scrolls up. (Per-session reset comes from keying the container by
    // sessionId, which remounts this hook.)
    const nearBottomRef = useRef(true);
    const lastTailRef = useRef(null);
    const handleScroll = () => {
        const el = containerRef.current;
        if (el === null)
            return;
        nearBottomRef.current =
            el.scrollTop + el.clientHeight >= el.scrollHeight - NEAR_BOTTOM_THRESHOLD_PX;
    };
    useEffect(() => {
        if (tailKey === null || tailKey === lastTailRef.current)
            return;
        lastTailRef.current = tailKey;
        if (!nearBottomRef.current)
            return;
        bottomRef.current?.scrollIntoView?.({
            behavior: prefersReducedMotion() ? 'instant' : 'smooth',
            block: 'end',
        });
    }, [tailKey]);
    return { containerRef, bottomRef, handleScroll };
}
//# sourceMappingURL=stick-to-bottom.js.map